/**
 * REST fetch client for the heritage-app-backend.
 *
 * Adapted from `heritage-app/src/api/client.ts` for the web: it injects the
 * bearer token from localStorage, transparently refreshes on 401 and retries
 * once, and normalizes backend error payloads into a readable `Error.message`
 * so existing Chakra/toast error handling keeps working unchanged.
 */
import {
    getFromLocalStorage,
    saveToLocalStorage,
    deleteFromLocalStorage
} from '@/shared/utils/localStorageHelper'
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/shared/config/constants'
import { API_BASE_URL, ENDPOINTS } from './config'

export interface RequestOptions extends Omit<RequestInit, 'body'> {
    /** Skip attaching the bearer token (used for login / refresh). */
    skipAuth?: boolean
    /** JSON body — serialized automatically. */
    body?: unknown
    /** Internal: set after a refresh retry to avoid infinite loops. */
    _retriedAfterRefresh?: boolean
}

export class ApiError extends Error {
    status: number
    body: unknown
    constructor(message: string, status: number, body: unknown) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.body = body
    }
}

// Optional handler invoked when refresh fails / no refresh token — lets the
// React layer clear state and redirect. Registered by AuthProvider.
let onUnauthorized: () => void = () => {}
export const setUnauthorizedHandler = (handler: () => void) => {
    onUnauthorized = handler
}

export const tokenStore = {
    getAccessToken: () => getFromLocalStorage<string>(TOKEN_KEY),
    getRefreshToken: () => getFromLocalStorage<string>(REFRESH_TOKEN_KEY),
    setTokens: (accessToken: string, refreshToken?: string | null) => {
        saveToLocalStorage(TOKEN_KEY, accessToken)
        if (refreshToken) saveToLocalStorage(REFRESH_TOKEN_KEY, refreshToken)
    },
    clear: () => {
        deleteFromLocalStorage(TOKEN_KEY)
        deleteFromLocalStorage(REFRESH_TOKEN_KEY)
    }
}

function stringifyApiMessage(message: unknown): string | null {
    if (!message) return null
    if (typeof message === 'string') return message
    if (Array.isArray(message)) {
        return message
            .map((item) => stringifyApiMessage(item))
            .filter((item): item is string => !!item)
            .join('\n')
    }
    if (typeof message === 'object') {
        if ('message' in message) {
            return stringifyApiMessage((message as { message?: unknown }).message)
        }
        if ('error' in message) {
            return stringifyApiMessage((message as { error?: unknown }).error)
        }
    }
    return String(message)
}

function normalizeApiErrorMessage(parsed: unknown, rawBody: string): string | null {
    if (parsed && typeof parsed === 'object') {
        const errorJson = parsed as {
            message?: unknown
            error?: unknown
            details?: unknown
            error_description?: unknown
            errors?: unknown
        }
        const nested =
            Array.isArray(errorJson.errors) && errorJson.errors.length > 0
                ? stringifyApiMessage(errorJson.errors[0])
                : null
        const message =
            stringifyApiMessage(errorJson.message) ??
            stringifyApiMessage(errorJson.error_description) ??
            stringifyApiMessage(errorJson.details) ??
            nested ??
            stringifyApiMessage(errorJson.error)
        if (message) {
            const lower = message.toLowerCase()
            if (
                lower.includes('passwordstrengtherror') ||
                lower.includes('password is too weak') ||
                lower.includes('password strength')
            ) {
                return 'Password is too weak. Use at least 10 characters with uppercase and lowercase letters, plus a number.'
            }
            return message
        }
    }
    if (rawBody && rawBody.length < 500) return rawBody
    return null
}

async function attemptRefresh(): Promise<string | null> {
    const refreshToken = tokenStore.getRefreshToken()
    if (!refreshToken) return null
    try {
        const res = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        })
        if (!res.ok) return null
        const data = (await res.json()) as {
            accessToken?: string
            refreshToken?: string
        }
        if (!data.accessToken) return null
        tokenStore.setTokens(data.accessToken, data.refreshToken ?? refreshToken)
        return data.accessToken
    } catch {
        return null
    }
}

export async function request<T>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<T> {
    const { skipAuth, body, headers, _retriedAfterRefresh, ...rest } = options

    const finalHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(headers as Record<string, string>)
    }

    const token = tokenStore.getAccessToken()
    if (token && !skipAuth) {
        finalHeaders['Authorization'] = `Bearer ${token}`
    }

    const config: RequestInit = {
        ...rest,
        headers: finalHeaders,
        cache: 'no-store'
    }
    if (body !== undefined) {
        config.body = typeof body === 'string' ? body : JSON.stringify(body)
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config)

    if (!response.ok) {
        if (response.status === 401 && !skipAuth && !_retriedAfterRefresh) {
            const newToken = await attemptRefresh()
            if (newToken) {
                return request<T>(endpoint, { ...options, _retriedAfterRefresh: true })
            }
            tokenStore.clear()
            onUnauthorized()
            throw new ApiError('Session expired. Please log in again.', 401, null)
        }

        const rawBody = await response.text()
        let parsed: unknown = null
        try {
            parsed = rawBody ? JSON.parse(rawBody) : null
        } catch {
            parsed = null
        }
        const message =
            normalizeApiErrorMessage(parsed, rawBody) ??
            `Request failed: ${response.status} ${response.statusText}`
        throw new ApiError(message, response.status, parsed ?? rawBody)
    }

    // 204 / empty body
    if (response.status === 204) return {} as T
    const text = await response.text()
    return text ? (JSON.parse(text) as T) : ({} as T)
}

export const api = {
    get: <T>(endpoint: string, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: 'GET' }),
    post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: 'POST', body }),
    patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: 'PATCH', body }),
    put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: 'PUT', body }),
    delete: <T>(endpoint: string, options?: RequestOptions) =>
        request<T>(endpoint, { ...options, method: 'DELETE' })
}
